import { useEffect, useRef, useState } from 'react';
import Router from 'next/router';

interface UseIsLoadingNextRouteOptions {
	startDelayMs?: number;
	finishHoldMs?: number;
}

export const useIsLoadingBarNextRoute = (options: UseIsLoadingNextRouteOptions = {}) => {
	const { startDelayMs = 50, finishHoldMs = 500 } = options;
	const [visible, setVisible] = useState(false);
	const [progress, setProgress] = useState(0);

	const startTimerRef = useRef<NodeJS.Timeout | null>(null);
	const progressTimerRef = useRef<NodeJS.Timeout | null>(null);
	const finishingRef = useRef(false);

	const clearTimers = () => {
		if (startTimerRef.current) clearTimeout(startTimerRef.current);
		if (progressTimerRef.current) clearInterval(progressTimerRef.current);
		startTimerRef.current = null;
		progressTimerRef.current = null;
	};

	const start = () => {
		if (!startTimerRef.current) {
			startTimerRef.current = setTimeout(() => {
				finishingRef.current = false;
				setVisible(true);
				setProgress(0);

				progressTimerRef.current = setInterval(() => {
					setProgress((p) => {
						if (p >= 90) return p;
						const diff = 5 + Math.random() * 10;
						return Math.min(p + diff, 90);
					});
				}, 180);
			}, startDelayMs);
		}
	};

	const stop = () => {
		if (finishingRef.current) return;
		finishingRef.current = true;

		clearTimers();
		setProgress(100);
		setTimeout(() => {
			setVisible(false);
			setProgress(0);
			finishingRef.current = false;
		}, finishHoldMs);
	};

	useEffect(() => {
		const handleStart = () => start();
		const handleStop = () => stop();

		Router.events.on('routeChangeStart', handleStart);
		Router.events.on('routeChangeComplete', handleStop);
		Router.events.on('routeChangeError', handleStop);

		return () => {
			Router.events.off('routeChangeStart', handleStart);
			Router.events.off('routeChangeComplete', handleStop);
			Router.events.off('routeChangeError', handleStop);
			clearTimers();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { visible, progress };
};
